const SOUND_KEY = "sps_sound_on";
export const loadSoundPref = () => localStorage.getItem(SOUND_KEY) !== "off";
export const saveSoundPref = (on) => localStorage.setItem(SOUND_KEY, on ? "on" : "off");

// NOTE ON STORAGE: localStorage here holds only non-sensitive audio comfort
// preferences (volume, mute, auto-narrate). Persisting them is an explicit
// trauma-informed product requirement so trainees keep their settings.
export const devLog = (...args) => {
  if (process.env.NODE_ENV === "development") console.warn(...args);
};

const NARRATE_KEY = "sps_auto_narrate";
export const loadNarratePref = () => localStorage.getItem(NARRATE_KEY) === "on";
export const saveNarratePref = (on) => localStorage.setItem(NARRATE_KEY, on ? "on" : "off");

// Master volume and mute, persisted so trainees keep their comfort settings.
const VOLUME_KEY = "sps_volume";
const MUTE_KEY = "sps_muted";
let masterVolume = Math.min(1, Math.max(0, parseFloat(localStorage.getItem(VOLUME_KEY) ?? "0.8") || 0.8));
let muted = localStorage.getItem(MUTE_KEY) === "on";
const prefListeners = new Set();
const liveMedia = new Set();

export const getVolume = () => masterVolume;
export const getMuted = () => muted;
export const effectiveVolume = () => (muted ? 0 : masterVolume);

export const onAudioPrefChange = (fn) => {
  prefListeners.add(fn);
  return () => prefListeners.delete(fn);
};

const applyToLive = () => {
  liveMedia.forEach(({ el, base }) => {
    el.volume = Math.min(1, base * effectiveVolume());
  });
  ambience.applyVolume();
};

const notifyPrefs = () => {
  prefListeners.forEach((fn) => fn({ volume: masterVolume, muted }));
  applyToLive();
};

export const setVolume = (v) => {
  masterVolume = Math.min(1, Math.max(0, v));
  localStorage.setItem(VOLUME_KEY, String(masterVolume));
  notifyPrefs();
};

export const setMuted = (m) => {
  muted = m;
  localStorage.setItem(MUTE_KEY, m ? "on" : "off");
  notifyPrefs();
};

// Wrap any Audio element so the master volume and mute apply to it live.
export const trackAudio = (el, base = 1) => {
  const entry = { el, base };
  el.volume = Math.min(1, base * effectiveVolume());
  liveMedia.add(entry);
  const drop = () => liveMedia.delete(entry);
  el.addEventListener("ended", drop);
  el.addEventListener("pause", drop);
  return el;
};

// Gentle heads-up shown on the briefing screen for cases with tougher sounds.
export const CONTENT_NOTES = {
  "hotline": "This case includes the sound of a baby crying in the background.",
  "full-shelter": "This case includes the sound of a baby crying in the background.",
  "shelter-kids": "This case includes background sounds of children in the shelter.",
  "shelter-ptsd": "This case includes a sudden door slam.",
  "shelter-dysregulation": "This case includes a distressed, raised voice.",
};

// Sensitive topic warnings, shown on the briefing screen before play begins.
export const TOPIC_NOTES = {
  "just-in-passing": "This case includes a survivor describing being strangled.",
  "hotline": "This case involves a survivor in immediate danger during a night call.",
  "shelter-ptsd": "This case portrays acute trauma responses, including flashbacks.",
};

const AMBIENCE = {
  "In-person": { lowpass: 420, gain: 0.05, hum: 120, humGain: 0.008 },
  "Phone": { lowpass: 300, gain: 0.045, hiss: 2600, hissGain: 0.006 },
  "Shelter intake": { lowpass: 340, gain: 0.06, hum: 60, humGain: 0.01 },
};

// Real recorded layers per scenario (sources: aporee/archive.org CC BY-SA post
// office ambience; archive.org public-domain night marsh; Wikimedia Commons CC0
// newborn cry; Commons "Slam closet door" by Gravity Sound).
const SCENARIO_LAYERS = {
  "walk-in": [{ src: "/sounds/office-ambience.mp3", volume: 0.16 }],
  "just-in-passing": [{ src: "/sounds/office-ambience.mp3", volume: 0.14 }],
  "money-trap": [{ src: "/sounds/office-ambience.mp3", volume: 0.14 }],
  "textline": [{ src: "/sounds/office-ambience.mp3", volume: 0.1 }],
  "third-party-call": [{ src: "/sounds/office-ambience.mp3", volume: 0.16 }],
  "hotline": [
    { src: "/sounds/night-ambience.mp3", volume: 0.1 },
    { src: "/sounds/child-background.mp3", volume: 0.14 },
  ],
  "full-shelter": [
    { src: "/sounds/night-ambience.mp3", volume: 0.08 },
    { src: "/sounds/child-background.mp3", volume: 0.12 },
  ],
  "shelter-kids": [
    { src: "/sounds/shelter-ambience.mp3", volume: 0.18 },
    { src: "/sounds/child-background.mp3", volume: 0.2 },
  ],
  "shelter-dysregulation": [{ src: "/sounds/shelter-ambience.mp3", volume: 0.18 }],
  "shelter-ptsd": [{ src: "/sounds/shelter-ambience.mp3", volume: 0.14 }],
};

// One-shot effects tied to specific decisions.
const DECISION_SFX = {
  "p2": { src: "/sounds/door-slam.mp3", volume: 0.55 },
};

export const playDecisionSfx = (decisionId) => {
  const sfx = DECISION_SFX[decisionId];
  if (!sfx) return;
  try {
    const a = trackAudio(new Audio(sfx.src), sfx.volume);
    a.play().catch(() => {});
  } catch (err) {
    devLog("SFX playback unavailable:", err);
  }
};

class Ambience {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.nodes = [];
    this.overlays = [];
  }

  _noiseBuffer(ctx) {
    const len = ctx.sampleRate * 4;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    return buf;
  }

  start(modality, scenarioId) {
    this.stop();
    try {
      const cfg = AMBIENCE[modality] || AMBIENCE["In-person"];
      if (!this.ctx) this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = this.ctx;
      if (ctx.state === "suspended") ctx.resume();

      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(effectiveVolume(), ctx.currentTime + 1.5);
      master.connect(ctx.destination);
      this.master = master;

      const src = ctx.createBufferSource();
      src.buffer = this._noiseBuffer(ctx);
      src.loop = true;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = cfg.lowpass;
      const g = ctx.createGain();
      g.gain.value = cfg.gain;
      src.connect(lp);
      lp.connect(g);
      g.connect(master);
      src.start();
      this.nodes = [src, lp, g, master];

      if (cfg.hum) {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = cfg.hum;
        const og = ctx.createGain();
        og.gain.value = cfg.humGain;
        osc.connect(og);
        og.connect(master);
        osc.start();
        this.nodes.push(osc, og);
      }
      if (cfg.hiss) {
        const s2 = ctx.createBufferSource();
        s2.buffer = this._noiseBuffer(ctx);
        s2.loop = true;
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = cfg.hiss;
        bp.Q.value = 0.7;
        const g2 = ctx.createGain();
        g2.gain.value = cfg.hissGain;
        s2.connect(bp);
        bp.connect(g2);
        g2.connect(master);
        s2.start();
        this.nodes.push(s2, bp, g2);
      }
    } catch {
      this.nodes = [];
    }
    (SCENARIO_LAYERS[scenarioId] || []).forEach((layer) => {
      try {
        const a = trackAudio(new Audio(layer.src), layer.volume);
        a.loop = true;
        a.play().catch(() => {});
        this.overlays.push(a);
      } catch (err) {
        devLog("Ambience layer unavailable:", layer.src, err);
      }
    });
  }

  applyVolume() {
    if (this.master && this.ctx) {
      try {
        this.master.gain.setTargetAtTime(effectiveVolume(), this.ctx.currentTime, 0.1);
      } catch (err) {
        devLog("Ambience volume update skipped:", err);
      }
    }
  }

  stop() {
    this.master = null;
    this.nodes.forEach((n) => {
      try {
        if (typeof n.stop === "function") n.stop();
        n.disconnect();
      } catch (err) {
        devLog("Ambience node already stopped:", err);
      }
    });
    this.nodes = [];
    this.overlays.forEach((a) => {
      try {
        a.pause();
      } catch (err) {
        devLog("Ambience overlay already stopped:", err);
      }
    });
    this.overlays = [];
  }
}

export const ambience = new Ambience();

